// ChallengeDetailPage — Single challenge detail
// Layout:
//   1. Top nav: < Back | "{Tier} Challenge" | Settings icon
//   2. Hero: account size, profit target, drawdown, profit share
//   3. Progress ring (filled % toward target)
//   4. Daily loss meter + max drawdown meter
//   5. Trading days counter
//   6. Activity (recent trades from bot or manual)
//   7. Reset challenge button
//   8. Activate AI bot CTA

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TIERS, DEMO_CHALLENGE, BOT_STRATEGIES, DEMO_BOT } from '../../data/platforms';
import { PlatformLogo, TierPill } from '../../components/Brand';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

export function ChallengeDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const challenge = DEMO_CHALLENGE; // would fetch by id in real flow
  const tier = TIERS[challenge.tier];
  const bot = DEMO_BOT;
  const strategy = BOT_STRATEGIES.find(s => s.key === bot.strategy)!;

  const progressPct = Math.min(100, (challenge.profit / challenge.targetProfit) * 100);
  const dailyPct = 30; // demo
  const ddPct = 12;    // demo

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">{challenge.tier} Challenge</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
        </button>
      </div>

      {/* Hero — white floating card with massive profit + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>Account balance</div>
          <div className="mt-1 flex items-baseline justify-center gap-2">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>${challenge.profit.toLocaleString()}</span>
            <button className="flex h-7 w-7 items-center justify-center text-tf-text-3">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            </button>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="font-semibold text-tf-profit">+{progressPct.toFixed(2)}%</span>
            <span className="text-tf-text-3">(+${challenge.profit.toLocaleString()})</span>
            <span className="text-tf-text-3">· Today</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => nav('/portal/auto-flatten')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Hold
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => nav('/portal/reset')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Reset
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
        </TfCardFloating>
      </div>

      {/* Stat row: profit target, daily pause, max DD, profit share */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Target', value: `$${(challenge.targetProfit / 1000).toFixed(0)}K`, color: '#34C759' },
          { label: 'Daily', value: `$${(challenge.dailyDrawdown / 1000).toFixed(0)}K`, color: '#FF9500' },
          { label: 'Max DD', value: `$${(challenge.maxDrawdown / 1000).toFixed(0)}K`, color: '#FF3B30' },
        ]}
      />

      {/* Progress ring with profit target context */}
      <div className="mx-4 mt-3">
        <TfCard className="p-4">
          <div className="flex items-center gap-4">
            <ProgressRing value={progressPct} color="#007AFF" bg="rgba(0,122,255,0.15)" size={68}>
              <span className="text-[15px] font-bold tabular-nums text-tf-text-1">{progressPct.toFixed(0)}%</span>
            </ProgressRing>
            <div className="flex-1">
              <div className="text-[12px] text-tf-text-3">Profit target</div>
              <div className="text-[15px] font-semibold text-tf-text-1">${challenge.profit.toLocaleString()} / ${challenge.targetProfit.toLocaleString()}</div>
              <div className="text-[12px] text-tf-text-3 mt-0.5">{tier.profitSplit}% to you · {challenge.profitShare}% split</div>
            </div>
          </div>
        </TfCard>
      </div>

      {/* Meters row */}
      <div className="mx-4 mt-3 grid grid-cols-2 gap-2">
        <MeterCard label="Daily loss" used={dailyPct} amount={dailyPct * challenge.dailyDrawdown / 100} limit={challenge.dailyDrawdown} color="#FF9500" />
        <MeterCard label="Max drawdown" used={ddPct} amount={ddPct * challenge.maxDrawdown / 100} limit={challenge.maxDrawdown} color="#FF3B30" />
      </div>

      {/* Challenge terms */}
      <TfSectionHeaderView title="Challenge terms" />
      <div className="mx-4">
        <TfCard>
          <Row label="Account size" value={`$${challenge.size}K funded`} />
          <Row label="Tier" value={<TierPill tier={challenge.tier} />} />
          <Row label="Platform" value={challenge.platformDisplay} />
          <Row label="Profit target" value={`$${challenge.targetProfit.toLocaleString()}`} />
          <Row label="Max drawdown" value={`$${challenge.maxDrawdown.toLocaleString()}`} />
          <Row label="Daily loss limit" value={`$${challenge.dailyDrawdown.toLocaleString()}`} />
          <Row label="Funded days minimum" value={`${tier.fundedDaysMin} days`} />
          <Row label="Profit split" value={`${tier.profitSplit}% to you`} />
          <Row label="Day" value={`${challenge.daysActive} of ${challenge.daysRemaining + challenge.daysActive}`} />
          <Row label="Status" value={<span className="text-tf-profit font-semibold capitalize">{challenge.status}</span>} last />
        </TfCard>
      </div>

      {/* AI Bot CTA */}
      <div className="mx-4 mt-3">
        <TfCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-tf-text-1 text-[22px]">{strategy.icon}</div>
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-tf-text-1">AI Bot Trader</div>
              <div className="text-[12px] text-tf-text-3">{strategy.name} · {bot.status} on this challenge</div>
            </div>
            <button onClick={() => nav('/portal/bot')} className="rounded-full bg-tf-text-1 px-4 py-1.5 text-[13px] font-semibold text-white">
              Open
            </button>
          </div>
        </TfCard>
      </div>

      {/* Reset challenge */}
      <TfSectionHeaderView title="Danger zone" />
      <div className="mx-4">
        <TfCard>
          <button className="flex w-full items-center justify-between px-4 py-3 text-left active:bg-tf-red/5">
            <div>
              <div className="text-[14px] font-semibold text-tf-loss">Reset challenge</div>
              <div className="text-[12px] text-tf-text-3">Start over for $69 (reset fee)</div>
            </div>
            <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </TfCard>
      </div>
    </div>
  );
}

function ProgressRing({ value, color, bg, size = 64, children }: { value: number; color: string; bg: string; size?: number; children?: React.ReactNode }) {
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={bg} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      <g transform={`rotate(90 ${size / 2} ${size / 2})`}>
        <foreignObject x="0" y="0" width={size} height={size}>
          <div className="flex h-full w-full items-center justify-center">{children}</div>
        </foreignObject>
      </g>
    </svg>
  );
}

function MeterCard({ label, used, amount, limit, color }: { label: string; used: number; amount: number; limit: number; color: string }) {
  return (
    <TfCard className="p-3">
      <div className="text-[12px] text-tf-text-3">{label}</div>
      <div className="mt-0.5 text-[15px] font-bold tabular-nums text-tf-text-1">${amount.toFixed(0)} <span className="text-[11px] text-tf-text-3 font-normal">/ ${limit.toLocaleString()}</span></div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-tf-surface-1">
        <div className="h-full rounded-full" style={{ width: used + '%', backgroundColor: color }} />
      </div>
      <div className="mt-1 text-[11px] font-semibold tabular-nums text-tf-text-1">{used}% used</div>
    </TfCard>
  );
}

function Row({ label, value, last }: { label: string; value: React.ReactNode; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[14px] text-tf-text-3">{label}</span>
      <span className="text-[14px] font-medium text-tf-text-1">{value}</span>
    </div>
  );
}
